/**
 * A visit's price, said the way it bills — `docs/proposal.md` §6.3.
 *
 * The same `$864` three ways. A customer on a monthly plan reads **$288 a
 * month for 3 months**, one buying per visit reads **$864** with "$96 per
 * visit × 9", and "Both" reads the plan figure with the arithmetic quietly
 * under it. **The total never changes** — only its wording does — which is
 * why every figure here comes off the `ProposalPrice` the server resolved
 * rather than being worked out a second time on this side.
 *
 * The important rule is the quiet one: **a per-visit or per-service figure is
 * drawn only where the visit is priced per visit, or is a single visit**
 * (`serviceShowsPrice`). In the plan view a list of $96s under a headline of
 * $288 a month is two prices for one thing, and the customer will add up the
 * wrong one.
 */
import type { ProposalVisit } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollarsExact } from '../../lib/format.js'

/**
 * May a service or a line show its own figure on this visit?
 *
 * Exported because the service rows ask it too, and two readings of "is this
 * priced per visit" would be two answers on one card.
 */
export const serviceShowsPrice = (visit: ProposalVisit): boolean =>
  visit.price.view !== 'plan' || (visit.count.n ?? 1) <= 1

export function VisitPrice({ visit, layout, muted = false, plus = false }: {
  visit: ProposalVisit
  /**
   * `band` is the right-hand column of the band (board 3), `phone` its own
   * row under a rule with the sentence at the right (board 8), `compact` the
   * smaller figure an option card uses.
   */
  layout: 'band' | 'phone' | 'compact'
  /** An optional visit nobody has added: the figure goes grey (board 4). */
  muted?: boolean
  /** An optional visit says what it *adds*: "+$640". */
  plus?: boolean
}) {
  const said = sayPrice(visit, plus)
  const figureSize = layout === 'band' ? 'text-[28px]' : layout === 'phone' ? 'text-[22px]' : 'text-[24px]'
  const unitSize = layout === 'band' ? 'text-[14.5px]' : 'text-[13px]'
  const subSize = layout === 'band' ? 'text-[13.5px]' : 'text-[12.5px]'

  const figure = (
    <span className="inline-flex items-baseline whitespace-nowrap">
      <b className={cn('tnum font-bold tracking-[-0.02em]', figureSize,
        muted ? 'text-text-muted' : 'text-text')}>
        {said.figure}
      </b>
      {said.unit && (
        <span className={cn('ml-[5px] whitespace-nowrap font-semibold text-text-body', unitSize)}>
          {said.unit}
        </span>
      )}
    </span>
  )

  const notes = (
    <>
      {said.sub && <span className={cn('tnum text-text-body', subSize)}>{said.sub}</span>}
      {said.quiet && <span className={cn('tnum text-text-faint', subSize)}>{said.quiet}</span>}
      {said.cap && <span className="text-[12.5px] text-text-faint">{said.cap}</span>}
    </>
  )

  if (layout === 'phone') {
    return (
      <div className="mt-3 flex items-baseline justify-between gap-2.5 border-t border-brand-ln pt-3">
        <div className="flex shrink-0 flex-col items-start gap-px">{figure}</div>
        <span className="flex flex-col items-end gap-px text-right">{notes}</span>
      </div>
    )
  }

  return (
    <span className={cn('flex shrink-0 flex-col gap-px',
      layout === 'band' ? 'items-end text-right' : 'items-start text-left')}>
      {figure}
      {notes}
    </span>
  )
}

/**
 * The words of one visit's price — board 12, row by row.
 *
 * - **Period** (the default for recurring work): `$288 / month`, "for 3 months".
 * - **Rental, open-ended**: `$120 / week`, "until the rental ends".
 * - **After each visit**: `$96 per visit`, "9 visits, billed as each is done".
 * - **Deposit, milestones**: the total, and the plan's own sentence.
 * - **Time and materials**: `$840 estimate`, how it is worked out, and the cap.
 *
 * Per visit and Both are the same money said again: per visit leads with the
 * total and puts "$96 per visit × 9" under it; Both keeps the plan figure and
 * adds "9 visits × $96 = $864" quietly beneath.
 */
export function sayPrice(visit: ProposalVisit, plus = false): {
  figure: string
  unit: string | null
  sub: string | null
  quiet: string | null
  cap: string | null
} {
  const price = visit.price
  const sign = plus ? '+' : ''
  const cap = price.plan.estimate && price.plan.cap !== null
    ? `Never more than ${formatDollarsExact(price.plan.cap)} without asking you first`
    : null

  /*
   * Open-ended work has no total to show, because there is no number of
   * trips to multiply by: `$120 / week` is the whole truth and the total
   * card says the rest. Adding it into a sum would be inventing an end date.
   */
  if (visit.count.openEnded && visit.recurring) {
    return {
      figure: `${sign}${formatDollarsExact(visit.recurring.amount)}`,
      unit: `/ ${visit.recurring.label}`,
      /* Board 12's words where the plan has none of its own. */
      sub: price.plan.text || 'until the rental ends',
      quiet: null,
      cap,
    }
  }

  const n = price.occurrences
  const each = formatDollarsExact(price.perVisit)

  if (price.view === 'visit') {
    return {
      figure: `${sign}${formatDollarsExact(price.total)}`,
      unit: price.plan.estimate ? 'estimate' : null,
      sub: n > 1 ? `${each} per visit × ${n}` : (plus ? price.plan.text || null : null),
      quiet: null,
      cap,
    }
  }

  if (price.plan.unit) {
    return {
      figure: `${sign}${formatDollarsExact(price.plan.figure)}`,
      unit: price.plan.estimate ? 'estimate' : `/ ${unitWord(price.plan)}`,
      sub: price.plan.text || null,
      quiet: price.view === 'both' && n > 1
        ? `${n} visits × ${each} = ${formatDollarsExact(price.total)}` : null,
      cap,
    }
  }

  if (price.plan.kind === 'completion' && n > 1) {
    return {
      figure: `${sign}${each}`,
      unit: 'per visit',
      sub: price.plan.text || null,
      quiet: price.view === 'both' ? `${n} × ${each} = ${formatDollarsExact(price.total)}` : null,
      cap,
    }
  }

  return {
    figure: `${sign}${formatDollarsExact(price.total)}`,
    unit: price.plan.estimate ? 'estimate' : null,
    sub: price.plan.text || null,
    quiet: null,
    cap,
  }
}

/** "every 2 weeks" rather than "week", where the plan says so. */
function unitWord(plan: ProposalVisit['price']['plan']): string {
  return plan.every > 1 ? `${plan.every} ${plan.unit}s` : plan.unit ?? 'period'
}

/**
 * The same money in one line, for a recap rather than a card (§8.1 item 2).
 *
 * The sign card lists what is included beside the signature, and a 22px
 * three-line price block per row would be the visit cards again. It is the
 * **same `ProposalPrice`**, said shorter — so the recap and the card above it
 * cannot disagree about how a visit bills, which on the one screen where
 * somebody is about to sign is the disagreement that matters most.
 */
export function priceLine(visit: ProposalVisit): string {
  const price = visit.price
  if (visit.count.openEnded && visit.recurring) {
    return `${formatDollarsExact(visit.recurring.amount)} / ${visit.recurring.label}`
  }
  if (price.view !== 'visit' && price.plan.unit) {
    const per = `${formatDollarsExact(price.plan.figure)} a ${unitWord(price.plan)}`
    return price.plan.periods
      ? `${per} for ${price.plan.periods} ${price.plan.unit}${price.plan.periods === 1 ? '' : 's'}`
      : per
  }
  return price.occurrences > 1
    ? `${formatDollarsExact(price.total)} · ${formatDollarsExact(price.perVisit)} a visit`
    : formatDollarsExact(price.total)
}
